import React from "react";

import PropTypes from "prop-types";

import { I18n } from "libs/i18n";

/**
 * This component translates an error code returned by the API 400 response into a human-readable message.
 */
const ErrorCodeTranslation = ({
  errorCode = null,
  errorDetails = null,
  defaultMessage = null,
  onlyCodes = null,
  ignoreCodes = [],
  matrix = null,
}) => {
  /* eslint-disable max-len */
  const translationMatrix = {
    activation_fees_missing: <I18n id="topup-error-impossible-to-pay-activation-fees" vars={{ amount: errorDetails?.amount }} />,
    authentication_failed: <I18n id="input-error-auth-failed" />,
    barcode_registration_forbidden: <I18n id="barcode-form-input-error-forbidden" />,
    cannot_create_refund_request: <I18n id="refund-error-create-refund-request" />,
    cannot_create_refund_request_bank_account_not_found: <I18n id="refund-error-create-refund-request-no-bank-account" />,
    cannot_link_another_tag: <I18n id="tag-form-error-impossible-link-another-tag" />,
    cannot_remove_ticket_if_last: <I18n id="barcode-form-input-error-impossible-remove" />,
    cannot_remove_ticket_if_tag: <I18n id="barcode-form-input-error-impossible-remove" />,
    credit_too_low_for_refund: <I18n id="refund-error-credit-too-low" />,
    different_amounts: <I18n id="topup-error-different-amounts" />,
    donation_not_enought_money: <I18n id="donation-error-credit-too-low" />,
    duplicate_refund_request: <I18n id="refund-error-already-exists" />,
    empty_donation: <I18n id="donation-error-empty-donation" />,
    incorrect_password: <I18n id="personal-info-password-incorrect" />,
    invalid_barcodes_combination: <I18n id="barcode-form-input-error-combination" />,
    invalid_credit: <I18n id="refund-error-credit-invalid" />,
    invalid_donation: <I18n id="donation-error-donation-invalid" />,
    invalid_products_quantity: (
      (
        errorCode === "invalid_products_quantity"
        && errorDetails?.products?.length
        && (errorDetails?.products[0].max_quantity_per_wallet !== null || errorDetails?.products[0].max_quantity !== null)
      )
        ? <I18n id="product-error-quantity-max-reached" />
        : <I18n id="product-error-quantity-invalid" />
    ),
    invalid_refund_request_status: <I18n id="refund-error-invalid-status" vars={{ sourceStatus: errorDetails?.source_status, targetStatus: errorDetails?.target_status }} />,
    invalid_short_tag: <I18n id="tag-form-error-invalid-short-tag" />,
    invalid_tag_information: <I18n id="fast-top-up-tag-form-screen-error-message" />,
    invalid_TopUpStatus: <I18n id="topup-error-invalid-status" />,
    max_credit_reached: <I18n id="topup-error-limit-reach-max" vars={{ amount: errorDetails?.amount }} />,
    max_credit_reached_2: <I18n id="topup-error-limit-reach-max-2" vars={{ amount: errorDetails?.amount }} />,
    max_purchase_reached: <I18n id="shop-page-error-max-purchase-reached" vars={{ max: errorDetails?.max_purchase_per_wallet }} />,
    maximum_wallets_creation_reached: <I18n id="wallet-creation-error-maximum-reached" />,
    min_top_up_amount_reached: <I18n id="topup-error-limit-reach-min" vars={{ amount: errorDetails?.amount }} />,
    multi_tickets_not_allowed: <I18n id="barcode-form-input-error-multi-ticket-not-allowed" />,
    quota_reached: <I18n id="cart-page-error-quota-reached" />,
    receipt_not_downloadable_transaction_not_fiscalized: <I18n id="receipt-page-pdf-modal-message-error-transaction-not-fiscalized" />,
    refund_request_already_held: <I18n id="refund-error-already-on-hold" />,
    refund_not_authorized: <I18n id="refund-error-not-authorized" />,
    tag_already_in_use: <I18n id="tag-form-error-short-tag-already-in-use" />,
    tag_already_paired: <I18n id="tag-form-error-short-tag-already-paired" />,
    tag_not_reusable: <I18n id="tag-form-error-short-tag-already-in-use" />,
    tag_with_no_wallet: <I18n id="tag-form-error-tag-with-no-wallet" />,
    ticket_already_linked_to_another_wallet: <I18n id="barcode-form-input-error-already-linked" />,
    ticket_already_paired: (
      <>
        <I18n id="barcode-form-input-error-already-paired" />
        <> </>
        <I18n id="barcode-form-input-error-try-to-use-your-chip" />
      </>
    ),
    ticket_already_scanned: (
      <>
        <I18n id="barcode-form-input-error-already-scanned" />
        <> </>
        <I18n id="barcode-form-input-error-try-to-use-your-chip" />
      </>
    ),
    ticket_barcode_invalid: <I18n id="barcode-form-input-error-invalid" />,
    ticket_processing_retry_later: <I18n id="barcode-form-input-error-cannot-be-linked-retry-later" />,
    ticket_removed_or_from_a_previous_event: <I18n id="barcode-form-input-error-not-valid-anymore" />,
    unauthorized_top_up: <I18n id="topup-error-unauthorized" />,
    unique: <I18n id="input-error-code-unique" />,
    wallet_no_card: <I18n id="wallet-auto-topup-error-no-card-saved" />,
    /* eslint-enable */
    ...matrix,
  };

  if ((onlyCodes && !onlyCodes.includes(errorCode)) || ignoreCodes.includes(errorCode)) {
    return errorCode;
  }

  return translationMatrix[errorCode] || defaultMessage || errorCode;
};

ErrorCodeTranslation.propTypes = {
  /**
   * The error code to translate.
   */
  errorCode: PropTypes.string,
  /**
   * The error details to include or compute in the translation (for example the name of the field,
   * the invalid value, the min length...).
   */
  errorDetails: PropTypes.object,
  /**
   * The default message to return if no relation found with the given errorCode.
   */
  defaultMessage: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  /**
   * If we want to translate only a restricted list of codes (the other can return a default error message
   * or the errorCode itself).
   */
  onlyCodes: PropTypes.arrayOf(PropTypes.string),
  /**
   * If we want to translate all codes managed except a restricted list of codes (for which we will return
   * a default error message or the errorCode itself).
   */
  ignoreCodes: PropTypes.arrayOf(PropTypes.string),
  /**
   * The correlation table between the errorCode and the translation to return.
   * It will override/be merged to the default matrix implemented, so it won't erase the existing keys.
   */
  matrix: PropTypes.object,
};

export default ErrorCodeTranslation;
